import { useTokens, useTheme } from './theme';
import { CheckGlyph } from './uiKit.jsx';

/**
 * One unified boolean/segmented toggle — replaces the ad-hoc Toggle in
 * AdminsPanel and OveragePanel. Two forms:
 *
 *   Boolean:   <Toggle checked={on} onChange={(next) => ...} label="Enabled" />
 *     Renders a single pill; onChange receives the NEXT boolean.
 *
 *   Segmented: <Toggle options={[{ value, label }]} value={role}
 *                       onChange={(v) => ...} label="Role" />
 *     Single-select by default. Pass `multiple` for a checkbox-style group where
 *     `value` is an array and onChange receives the NEXT array.
 *
 * `options` entries may be strings or { value, label } objects.
 *
 * @param {{
 *   options?: Array<string | { value: any, label: React.ReactNode }>,
 *   checked?: boolean,
 *   value?: any,
 *   multiple?: boolean,
 *   onChange?: (next: any) => void,
 *   label?: React.ReactNode,
 *   disabled?: boolean, // boolean form only
 * }} props
 */
export function Toggle({ options, checked, value, multiple, onChange, label, disabled = false }) {
  const t = useTokens();
  // Portal: a check marks the selected chip, so selection is never color alone.
  const portal = useTheme() === 'portal';

  const pillStyle = (active) => {
    return {
      backgroundColor: active ? t.pillActiveBg : t.pillBg,
      color: active ? t.pillActiveText : t.pillText,
      borderColor: active ? t.pillActiveBorder : t.pillBorder,
      fontFamily: t.fonts.body,
    };
  };
  const pillClass = `inline-flex items-center min-h-[44px] rounded-full border px-4 text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`;

  const caption = label ? (
    <div className="text-xs mb-1" style={{ fontFamily: t.fonts.mono, color: t.muted }}>{label}</div>
  ) : null;

  // Boolean form.
  if (!options) {
    return (
      <div className="flex flex-col items-start">
        {caption}
        <button
          type="button"
          aria-pressed={!!checked}
          disabled={disabled}
          onClick={() => { return onChange?.(!checked); }}
          className={disabled ? `${pillClass} cursor-not-allowed opacity-60` : pillClass}
          style={pillStyle(!!checked)}
        >
          {portal && checked ? <CheckGlyph /> : null}
          {checked ? 'On' : 'Off'}
        </button>
      </div>
    );
  }

  // Segmented form.
  const opts = options.map((o) => {
    return typeof o === 'object' && o !== null ? o : { value: o, label: o };
  });
  const isActive = (v) => {
    return multiple ? Array.isArray(value) && value.includes(v) : value === v;
  };
  const handle = (v) => {
    if (!multiple) { onChange?.(v); return; }
    const arr = Array.isArray(value) ? value : [];
    onChange?.(arr.includes(v) ? arr.filter((x) => { return x !== v; }) : [...arr, v]);
  };

  return (
    <div>
      {caption}
      <div className="flex flex-wrap" role="group" aria-label={typeof label === 'string' ? label : undefined}>
        {opts.map((o) => {
          const active = isActive(o.value);
          return (
            <button
              key={String(o.value)}
              type="button"
              aria-pressed={active}
              onClick={() => { return handle(o.value); }}
              className={`${pillClass} mr-2 mb-1`}
              style={pillStyle(active)}
            >
              {portal && active ? <CheckGlyph /> : null}
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default Toggle;
